// The three axes as navigation.
//
// USAGE / ENGAGEMENT / COUPLING is already the load-bearing product decision on
// this screen — `AnalyticsScreen.tsx`'s header says *"do not add a panel here
// without deciding which axis it belongs to"* — and it was a heading, which is
// a rule a new panel can quietly ignore. As a URL it cannot be ignored: a panel
// has to be added to one of three route files.
//
// It also gives the monthly report email somewhere specific to point. "Your
// coupling report" linking to a page that opens at the top of a fourteen-panel
// scroll is a worse link than no link.
//
// The overview keeps all three axes. These routes are the same axis with the
// heavy views attached — the ones that need a full-width canvas and would make
// the overview unscrollable. The sidebar keeps ONE Analytics row; `hrefs.ts`'s
// longest-prefix rule already lights it for every child path.

import { Link } from "@tanstack/react-router";
import { hrefs } from "../../shell/hrefs";
import {
  AXIS_COUPLING_TITLE,
  AXIS_ENGAGEMENT_TITLE,
  AXIS_USAGE_TITLE,
} from "./metricLabels";
import type { RangeDays } from "./RangeChips";
import "./analytics.css";

export type AxisKey = "usage" | "engagement" | "coupling";

export type AxisNavProps = {
  orgSlug: string;
  days: RangeDays;
  /** Undefined on the overview, which is a destination of its own. */
  active?: AxisKey;
};

export function AxisNav({ orgSlug, days, active }: AxisNavProps) {
  const to = hrefs(orgSlug);
  const destinations: Array<{ key: AxisKey | "overview"; label: string; href: string }> = [
    { key: "overview", label: "Overview", href: to.analytics },
    { key: "usage", label: AXIS_USAGE_TITLE, href: to.analyticsUsage },
    { key: "engagement", label: AXIS_ENGAGEMENT_TITLE, href: to.analyticsEngagement },
    { key: "coupling", label: AXIS_COUPLING_TITLE, href: to.analyticsCoupling },
  ];

  return (
    <nav className="maisight-analytics-axisnav" aria-label="Analytics sections">
      {destinations.map((item) => (
        <Link
          key={item.key}
          to={item.href}
          // The range travels with the link, so switching axis keeps the window
          // being looked at instead of silently resetting it to the default.
          search={{ days }}
          className="maisight-analytics-axisnav-item type-label"
          data-axis={item.key}
          aria-current={
            (active ?? "overview") === item.key ? "page" : undefined
          }
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );
}
